import React, { useState, useMemo, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

import { LAST_COORDINATES_STORED } from 'screens/Property/helpers/constants';
import { getStringParsed } from 'utilities/helpers';
import useMapReveal from 'screens/Property/hooks/useMapReveal';
import MapLoadingPlaceholder from 'screens/Property/components/MapLoadingPlaceholder';
import { calculateCentroidByEachProperties } from '../../helpers/mapApiHelpers';
import PropertyLandingContext from '../context';
import PropertyMapWrapper from './PropertyMapWrapper';

const propertyLandingMapDefaultPropOnSetShowSummaryPanel = () => () => {};
const PropertyLandingMap = ({
  properties = null,
  onSetShowSummaryPanel = propertyLandingMapDefaultPropOnSetShowSummaryPanel
}) => {
  const {
    selectedProperty,
    selectedField,
    propertyZoom,
    onPropertySetZoom,
    isLoadingFields,
    isLoadingProperties,
    fieldsAndAreasGeoJSONCollection: geoJSONCollection,
    showArchivedProperties
  } = useContext(PropertyLandingContext);

  const [manualCoords, setManualCoords] = useState(
    getStringParsed(localStorage.getItem(LAST_COORDINATES_STORED))
  );

  // Track the number of features to avoid recomputing centroid when only
  // $layer (highlight) changes — the geometries haven't moved.
  const featureCount = geoJSONCollection?.features?.length ?? 0;
  // Derive a signature that changes only when geometries change,
  // ignoring non-geometry properties like $layer.
  const geometrySignature = useMemo(() => {
    const features = geoJSONCollection?.features;
    if (!features || !Array.isArray(features)) {
      return '';
    }

    try {
      // Only include geometry in the signature so that style/highlight
      // changes in feature properties do not trigger a centroid recompute.
      const geometries = features.map(feature => feature?.geometry ?? null);
      return JSON.stringify(geometries);
    } catch (e) {
      // In case of unexpected circular structures or serialization issues,
      // fall back to an empty signature to avoid breaking rendering.
      return '';
    }
  }, [geoJSONCollection]);

  const geoJSONCentroid = useMemo(() => {
    const centroid = calculateCentroidByEachProperties(
      geoJSONCollection,
      properties
    );

    return JSON.stringify(centroid);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureCount, properties, geometrySignature]);

  const mapCoordinates = useMemo(
    () => manualCoords || JSON.parse(geoJSONCentroid)?.geometry?.coordinates,
    [geoJSONCentroid, manualCoords]
  );

  useEffect(() => {
    if (selectedField?.length > 0) {
      onSetShowSummaryPanel(true)();
    }
  }, [selectedField, onSetShowSummaryPanel]);

  const isWaitingForData = isLoadingProperties || isLoadingFields;
  const { revealMap, mapRevealClassName, showPlaceholder } = useMapReveal(
    !isWaitingForData && !geoJSONCollection?.features?.length,
    isWaitingForData
  );

  const labelName = (selectedProperty?.type || 'org')
    .toLowerCase()
    .replace(/\s+/g, '');

  return (
    <div className="relative w-full h-full">
      <MapLoadingPlaceholder show={showPlaceholder} />
      <div
        data-testid="property-landing-map"
        className={`relative w-full h-full flex flex-col overflow-y-auto ${mapRevealClassName}`}
      >
        <PropertyMapWrapper
          onFit={revealMap}
          fieldsLoading={isLoadingFields}
          zoom={propertyZoom}
          setZoom={onPropertySetZoom}
          currentCoordinates={mapCoordinates}
          setCurrentCoordinates={setManualCoords}
          displayOnly
          geoJSONCollection={geoJSONCollection}
          labelName={labelName}
          selectedField={selectedField}
          showArchivedLabels={!!showArchivedProperties}
          stretch
        />
      </div>
    </div>
  );
};

PropertyLandingMap.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  onSetShowSummaryPanel: PropTypes.func
};

export default PropertyLandingMap;
